import type { CountyAdapter, ParsedDocument } from "../types";

interface LapeerBuilder extends ParsedDocument {
  hasReferral: boolean;
  parcelId: string;
  bookPage: string;
  legalParts: string[];
}

const parseDate = (s: string): string => {
  const p = s.trim().split("/");
  if (p.length === 3) {
    const [m, d, y] = p;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return s;
};

const parse = (text: string): ParsedDocument[] => {
  const lines = text.split("\n");
  const docs = new Map<string, LapeerBuilder>();
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trimEnd();
    const headerMatch = line.match(
      /^\s*(\d{2}\/\d{2}\/\d{4})\s+(\d{5}-\d{5})\s+(\S+)\s+(.*?)\s*$/,
    );
    if (!headerMatch) {
      i++;
      continue;
    }

    const filingDate = headerMatch[1];
    const bookPage = headerMatch[2];
    const docReference = headerMatch[3];
    const documentType = headerMatch[4].trim();

    const blockLines: string[] = [];
    i++;
    let referralLine = "";
    while (i < lines.length) {
      const next = lines[i];
      if (next.includes(">>>>>>")) {
        referralLine = next;
        i++;
        break;
      }
      if (next.match(/^\s*\d{2}\/\d{2}\/\d{4}\s+\d{5}-\d{5}/)) break;
      if (next.match(/^\s{50,}\d{2,3}\s+\d{3}\s+\d{3}\s+\d{2}\s*$/)) {
        i++;
        break;
      }
      blockLines.push(next);
      i++;
    }

    const hasReferral = referralLine.toUpperCase().includes("REFERRAL");

    if (!docs.has(docReference)) {
      docs.set(docReference, {
        documentNumber: docReference,
        documentType,
        filingDate: parseDate(filingDate),
        grantors: [],
        grantees: [],
        legalParts: [],
        hasReferral: false,
        parcelId: "",
        bookPage,
      });
    }
    const doc = docs.get(docReference)!;
    if (hasReferral) doc.hasReferral = true;

    const parcelMatch = referralLine.match(
      /(\d{2,3}\s+\d{2,3}\s+\d{3}\s+\d{3}\s+\d{2})\s*$/,
    );
    if (parcelMatch && !doc.parcelId) doc.parcelId = parcelMatch[1].trim();

    for (const bl of blockLines) {
      const t = bl.trim();
      const grantor = t.match(/^(?:X\s+)?GRANTOR\s+(.*)/i);
      if (grantor) {
        const n = grantor[1].trim();
        if (n && !doc.grantors.includes(n)) doc.grantors.push(n);
        continue;
      }
      const grantee = t.match(/^(?:X\s+)?GRANTEE\s+(.*)/i);
      if (grantee) {
        const n = grantee[1].trim();
        if (n && !doc.grantees.includes(n)) doc.grantees.push(n);
        continue;
      }
      const addr = t.match(/^(?:X\s+)?ADDRESS\s+(.*)/i);
      if (addr) {
        const a = addr[1].trim();
        if (a && !doc.legalParts.includes(a)) doc.legalParts.push(a);
        continue;
      }
      if (t.match(/^(SEC\s+\d|LOT\s+\d)/i)) {
        if (!doc.legalParts.includes(t)) doc.legalParts.push(t);
        continue;
      }
      if (
        t.match(/\b(PLAT|SUB|ADDITION|DIVISION)\b/i) &&
        !t.match(/^(GRANTOR|GRANTEE|ADDRESS)/i)
      ) {
        if (!doc.legalParts.includes(t)) doc.legalParts.push(t);
      }
    }
  }

  return Array.from(docs.values()).map((d) => ({
    documentNumber: d.documentNumber,
    documentType: d.documentType,
    filingDate: d.filingDate,
    grantors: d.grantors,
    grantees: d.grantees,
    legalDescription: d.legalParts.join(" | ") || undefined,
    notes: [
      d.parcelId ? `Parcel: ${d.parcelId}` : null,
      `Book-Page: ${d.bookPage}`,
    ]
      .filter(Boolean)
      .join(" | "),
    skipReason: d.hasReferral ? "referral" : undefined,
  }));
};

export const lapeerAdapter: CountyAdapter = {
  countyId: "lapeer",
  countyLabel: "Lapeer County, MI - Land Contract Parser",
  description:
    "Paste search results from Lapeer County Register of Deeds. Automatically deduplicates by document reference, filters out records with referrals, family transfers, business-to-business, and bank transactions.",
  placeholder: `Paste Lapeer County search results here...

Expected format (multi-line blocks per document):
   12/29/2022    03332-00511   1C-3009           MEMORANDUM LAND CONTRACT
   GRANTOR          RIEBLING, KURT F.
 X GRANTEE          THAO, DEBIE
   LOT 78  (PART OF)                                    CITY OF LAPEER
>>>>>> ADDITIONAL PARTIES AND DESCRIPTIONS

Records with "REFERRAL" in the >>>>>> line will be excluded.`,
  detectionReasons: ["Memorandum Land Contract", "Lapeer County ROD"],
  filterRules: [
    "Referrals: Records with \"REFERRAL\" in the additional info line are filtered out",
    "Family transfers: Same last name on grantor/grantee sides are filtered out",
    "Business-to-business: Both parties are entities (LLC, Inc, Trust, etc.) are filtered out",
    "Bank transactions: Any party is a bank/mortgage company → filtered out",
    "Church/IRA: Any party is a church, ministry, or IRA → filtered out",
    "Duplicates: Multiple blocks per document are consolidated into one record",
  ],
  parse,
};
